/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { Initializer, Model } from '@adobe-commerce/elsie/lib';
import { initializeCart, refreshCart, resetCart } from '@/cart/api';
import { state } from '@/cart/lib/state';
import { events } from '@adobe-commerce/event-bus';
import { Lang } from '@adobe-commerce/elsie/i18n';
import { setPersistedCartData } from '@/cart/lib/persisted-data';
import { CartModel } from '@/cart/data/models';

type ConfigProps = {
  disableGuestCart?: boolean;
  langDefinitions?: Lang;

  models?: {
    CartModel?: Model<CartModel>;
  };
};

export const initialize = new Initializer<ConfigProps>({
  init: async (_config) => {
    const config = { disableGuestCart: false, ..._config };

    // Set config
    initialize.config.setConfig(config);

    initializeCart().catch(console.error);
  },

  listeners: () => [
    events.on(
      'authenticated',
      (authenticated) => {
        if (state.authenticated && !authenticated) {
          // Reset cart if the user is no longer authenticated
          events.emit('cart/reset', undefined);
        }
        // Login event occurred
        else if (authenticated && !state.authenticated) {
          state.authenticated = authenticated;
          initializeCart().catch(console.error);
        }
      },
      { eager: true }
    ),

    events.on('locale', async (locale) => {
      if (locale !== state.locale) {
        state.locale = locale;
        initializeCart().catch(console.error);
      }
    }),

    events.on('cart/reset', () => {
      resetCart().catch(console.error);
      events.emit('cart/data', null);
    }),

    events.on('cart/data', (payload) => {
      setPersistedCartData(payload);
    }),

    events.on('checkout/updated', (payload) => {
      if (!payload || payload?.type === 'quote') return;

      refreshCart().catch(console.error);
    }),

    events.on('requisitionList/alert', () => {
      refreshCart().catch(console.error);
    }),
  ],
});

export const config = initialize.config;
